import { For, Show, type Component } from 'solid-js';
import type { Strategy, StrategyAnalysis } from '~/domain/strategy';
import { layerColour } from '~/view/layers';

/**
 * The strategy under construction (SPEC-V2 §4.4) — the stack of layers shown
 * beside the catalogue, because adding a spread is a comparison between what
 * is already staked and what is being considered, and a comparison needs both
 * on screen at once rather than behind a modal or a tab switch.
 *
 * Two things this must never do. First, show money: chips are abstract counts
 * until a denomination is chosen at simulation time (§3.4), so nothing here
 * renders a dollar sign or calls `formatCents`. Second, claim the stack is
 * legal: table limits are money, and money does not exist yet, so whether a
 * pile of chips clears the house maximum is unknowable here — that check
 * belongs to Simulate, per entry, and is raised there, never implied here.
 */

export interface StrategyComposerProps {
  strategy: Strategy;
  /** From `analyseStrategy` — already resolved against the library and table. */
  analysis: StrategyAnalysis;
  dirty: boolean;
  onRename: (name: string) => void;
  onRemoveLayer: (spreadId: string) => void;
  onReorder: (from: number, to: number) => void;
  /** Hover a layer to dim the others on the felt and wheel. Null on leave. */
  onHoverLayer: (index: number | null) => void;
  onSave: () => void;
  class?: string;
}

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

const StrategyComposer: Component<StrategyComposerProps> = (props) => {
  const combined = () => props.analysis.combined;
  const bareCount = () => combined().wheel.pocketCount - combined().pocketsCovered;
  const isEmpty = () =>
    props.analysis.layers.length === 0 && props.analysis.missingSpreadIds.length === 0;

  /** The resolved layer an overlap's swatch belongs to, for its name in a tooltip. */
  const layerAt = (index: number) => props.analysis.layers[index];

  return (
    <section class={`rounded-lg border border-line bg-surface-1 p-3 ${props.class ?? ''}`}>
      <h2 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
        Strategy
      </h2>

      <div class="flex items-center gap-2">
        <input
          type="text"
          value={props.strategy.name}
          onInput={(event) => props.onRename(event.currentTarget.value)}
          aria-label="Strategy name"
          class="min-w-0 flex-1 rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink"
        />
        <button
          type="button"
          onClick={props.onSave}
          disabled={!props.dirty}
          title="Save this strategy"
          class="shrink-0 rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink-dim enabled:hover:border-line-strong enabled:hover:text-ink disabled:opacity-35"
        >
          {props.dirty ? 'Save' : 'Saved'}
        </button>
      </div>

      <Show
        when={!isEmpty()}
        fallback={
          <p class="mt-3 text-[11px] leading-relaxed text-ink-faint">
            No layers yet. Hover a spread in the catalogue to preview it stacked here, click to
            add it.
          </p>
        }
      >
        <ul class="mt-3 space-y-1">
          <For each={props.analysis.layers}>
            {(layer) => {
              const colour = () => layerColour(layer.index);
              return (
                <li
                  class="flex items-center gap-2 rounded border border-line bg-surface-2 px-2 py-1.5"
                  onMouseEnter={() => props.onHoverLayer(layer.index)}
                  onMouseLeave={() => props.onHoverLayer(null)}
                >
                  <span
                    class="h-3 w-3 shrink-0 rounded-full border"
                    style={{ 'background-color': colour().hue, 'border-color': colour().edge }}
                    title={colour().label}
                  />
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-[11px] font-medium text-ink">{layer.spread.name}</p>
                    <p class="tnum text-[10px] text-ink-faint">
                      {plural(layer.stakeChips, 'chip')} · {plural(layer.pocketsCovered, 'pocket')}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => props.onReorder(layer.index, layer.index - 1)}
                      disabled={layer.index === 0}
                      title="Move up"
                      aria-label={`Move ${layer.spread.name} up`}
                      class="rounded px-1 text-[10px] text-ink-faint enabled:hover:text-ink disabled:opacity-25"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      onClick={() => props.onReorder(layer.index, layer.index + 1)}
                      disabled={layer.index === props.analysis.layers.length - 1}
                      title="Move down"
                      aria-label={`Move ${layer.spread.name} down`}
                      class="rounded px-1 text-[10px] text-ink-faint enabled:hover:text-ink disabled:opacity-25"
                    >
                      ▼
                    </button>
                    <button
                      type="button"
                      onClick={() => props.onRemoveLayer(layer.spread.id)}
                      title="Remove this layer"
                      aria-label={`Remove ${layer.spread.name}`}
                      class="rounded px-1 text-[10px] text-ink-faint hover:text-loss"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              );
            }}
          </For>

          {/* Deleted out from under the strategy. Reported, not dropped — a
              strategy quietly playing five of its six layers is a lie about
              what was tested (SPEC-V2 §9, DECISIONS.md D33). */}
          <For each={props.analysis.missingSpreadIds}>
            {(spreadId) => (
              <li class="flex items-center justify-between gap-2 rounded border border-loss/40 bg-loss/10 px-2 py-1.5 text-[11px] text-loss">
                <span>Missing — this spread was deleted from the library</span>
                <button
                  type="button"
                  onClick={() => props.onRemoveLayer(spreadId)}
                  title="Remove this layer"
                  class="shrink-0 rounded px-1 text-[10px] text-loss underline decoration-loss/50 hover:text-ink"
                >
                  Remove
                </button>
              </li>
            )}
          </For>
        </ul>

        <div class="mt-3 border-t border-line pt-2">
          <h3 class="text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
            Stack
          </h3>
          <dl class="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
            <div>
              <dt class="text-ink-faint">Pockets covered</dt>
              <dd class="tnum text-ink">
                {combined().pocketsCovered}/{combined().wheel.pocketCount}
                <span class="ml-1 text-[10px] text-ink-faint">
                  ({combined().coveragePct.toFixed(1)}%)
                </span>
              </dd>
            </div>
            <div>
              <dt class="text-ink-faint">Total stake</dt>
              <dd class="tnum text-ink">{plural(combined().totalStake, 'chip')}</dd>
            </div>
            <div>
              <dt class="text-ink-faint">Bare pockets</dt>
              <dd class="tnum text-ink">{bareCount()}</dd>
            </div>
            <div>
              <dt class="text-ink-faint">Longest bare run</dt>
              <dd class="tnum text-ink">{combined().longestGap?.length ?? 0}</dd>
            </div>
          </dl>

          {/* Stacking is the moment the coverage figure above climbs, and the
              moment someone might read that as progress. The edge belongs
              right beside it, saying plainly that it has not moved (§9.1). */}
          <div class="mt-2.5 border-t border-line pt-2">
            <div class="flex items-baseline justify-between gap-2">
              <span class="text-[10px] uppercase tracking-[0.12em] text-ink-faint">
                Expected value
              </span>
              <span class="tnum text-[15px] font-semibold text-loss">
                {(combined().expectedValuePct * 100).toFixed(2)}%
              </span>
            </div>
            <p class="mt-1 text-[10px] leading-relaxed text-ink-faint">
              Of stake, every spin. Adding a spread widens the coverage above and changes
              the shape of what happens; it does not move this number. Only the odds on
              the table do.
            </p>
          </div>
        </div>

        <div class="mt-3 border-t border-line pt-2">
          <h3 class="text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
            Overlaps
          </h3>
          <Show
            when={props.analysis.overlaps.length > 0}
            fallback={
              <p class="mt-1 text-[11px] text-ink-faint">
                No target is staked by more than one layer.
              </p>
            }
          >
            <ul class="mt-1.5 space-y-1">
              <For each={props.analysis.overlaps}>
                {(overlap) => (
                  <li class="flex items-center justify-between gap-2 text-[11px]">
                    <div class="flex min-w-0 items-center gap-1">
                      <For each={overlap.layers}>
                        {(index) => {
                          const colour = layerColour(index);
                          const layer = layerAt(index);
                          return (
                            <span
                              class="h-2.5 w-2.5 shrink-0 rounded-full border"
                              style={{
                                'background-color': colour.hue,
                                'border-color': colour.edge,
                              }}
                              title={layer ? layer.spread.name : colour.label}
                            />
                          );
                        }}
                      </For>
                      <span class="truncate text-ink-dim">{overlap.label}</span>
                    </div>
                    <span class="tnum shrink-0 text-ink">{plural(overlap.totalChips, 'chip')}</span>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </div>
      </Show>
    </section>
  );
};

export default StrategyComposer;
